import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ChatEventTrigger, IChat, IChatMessage, IChatModal } from '../types/app-chat.type';

interface InitialState {
  isLoading: boolean;
  chats: Record<string, IChat>;
  hasMoreMessages: Record<string, boolean>;
  messages: Record<string, IChatMessage[]>;
  typing: Record<string, Set<number>>;
  activeChatId: string | null;
  hasUnreadMessages: boolean;
  onChatPage: boolean;
  eventTrigger: ChatEventTrigger | null;
  currentModal: IChatModal | null;
  lastSendMessageTimeStamp: number | null;
}

const chatInitialState: InitialState = {
  isLoading: false,
  chats: {},
  messages: {},
  typing: {},
  hasMoreMessages: {},
  activeChatId: null,
  hasUnreadMessages: false,
  onChatPage: false,
  eventTrigger: null,
  currentModal: null,
  lastSendMessageTimeStamp: null,
};

export const appChat = createSlice({
  name: 'appChat',
  initialState: chatInitialState,
  reducers: {
    setChats(state, action: PayloadAction<IChat[]>) {
      for (const chat of action.payload) state.chats[chat.id] = chat;
      state.hasUnreadMessages = action.payload.some((chat) => chat.unread_count > 0);
    },

    setCurrentModal(state, action: PayloadAction<IChatModal | null>) {
      state.currentModal = action.payload;
    },

    addChat(state, action: PayloadAction<IChat>) {
      state.chats[action.payload.id] = action.payload;
      if (!state.messages[action.payload.id]) state.messages[action.payload.id] = [];
      state.hasUnreadMessages = Object.values(state.chats).some((chat) => chat.unread_count > 0);
    },

    removeChat(state, action: PayloadAction<IChat>) {
      if (state.activeChatId === action.payload.id) state.activeChatId = null;
      delete state.chats[action.payload.id];
      delete state.messages[action.payload.id];
      delete state.hasMoreMessages[action.payload.id];
      state.hasUnreadMessages = Object.values(state.chats).some((chat) => chat.unread_count > 0);
    },

    setActiveChat(state, action: PayloadAction<string | null>) {
      state.activeChatId = action.payload;
    },

    setEventTrigger(state, action: PayloadAction<ChatEventTrigger | null>) {
      state.eventTrigger = action.payload;
    },

    addMessages(state, action: PayloadAction<{ chatId: string; messages: IChatMessage[]; append?: boolean }>) {
      const { chatId, messages } = action.payload;
      if (!state.messages[chatId]) state.messages[chatId] = [];
      const existingIds = new Set(state.messages[chatId].map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));
      if (action.payload.append) {
        state.messages[chatId].unshift(...newMessages);
      } else {
        state.messages[chatId].push(...newMessages);
      }
    },

    addOneMessage(state, action: PayloadAction<IChatMessage>) {
      const message = action.payload;
      if (!state.messages[message.chat_id]) state.messages[message.chat_id] = [];
      if (!state.messages[message.chat_id].find((x) => x.id === message.id)) {
        state.messages[message.chat_id].push(message);
      }
      const chat = state.chats[message.chat_id];
      if (chat) {
        chat.last_message = message;

        const participant = chat.participants.find((x) => x.participant_id === message.sender_id);
        if (participant) {
          participant.last_read_message_id = message.id;
          participant.last_read_message_at = message.created_at;
        }

        if (message.chat_id !== state.activeChatId) {
          if (!state.onChatPage) {
            state.hasUnreadMessages = true;
          }
          chat.unread_count += 1;
        }
      }
    },

    setTyping(state, action: PayloadAction<{ chatId: string; userId: number; isTyping: boolean }>) {
      const { chatId, userId, isTyping } = action.payload;
      if (!state.typing[chatId]) state.typing[chatId] = new Set();
      if (isTyping) {
        state.typing[chatId].add(userId);
      } else {
        state.typing[chatId].delete(userId);
      }
    },

    setHasMoreMessages(state, action: PayloadAction<{ chatId: string; hasMore: boolean }>) {
      const { chatId, hasMore } = action.payload;
      state.hasMoreMessages[chatId] = hasMore;
    },

    setOnChatPage(state, action: PayloadAction<boolean>) {
      state.onChatPage = action.payload;
    },

    setHasUnreadMessages(state, action: PayloadAction<boolean>) {
      state.hasUnreadMessages = action.payload;
    },

    setLastSendMessageTimeStamp(state, action: PayloadAction<number | null>) {
      state.lastSendMessageTimeStamp = action.payload;
    },

    clearState() {
      return chatInitialState;
    },
  },
});

export const {
  addOneMessage,
  addMessages,
  setActiveChat,
  setChats,
  setTyping,
  setOnChatPage,
  clearState,
  addChat,
  setEventTrigger,
  setHasUnreadMessages,
  setCurrentModal,
  setHasMoreMessages,
  removeChat,
  setLastSendMessageTimeStamp,
} = appChat.actions;
